import type {
  CatiCallDto,
  ChannelCampaignQuotaDto,
  ChannelCampaignQuotaRequestDto,
  ChannelProviderDto,
  EmailCampaignRequestDto,
  EmailCampaignResponseDto,
  GetChannelProvidersArgs,
  MessageCampaignRequestDto,
  MessageCampaignResponseDto,
  SmsCampaignRequestDto,
  SmsCampaignResponseDto,
} from './channel-campaigns.api.types';
import type {
  CatiCallSettings,
  ChannelCampaignQuota,
  ChannelProvider,
  EmailCampaignSettings,
  MessageCampaignSettings,
  SmsCampaignSettings,
} from './types';

function fromApiCampaignQuota(quota: ChannelCampaignQuotaDto | null): ChannelCampaignQuota {
  return {
    active: quota?.active ?? false,
    daily: quota?.daily ?? null,
    id: quota?.id ?? null,
    monthly: quota?.monthly ?? null,
    weekly: quota?.weekly ?? null,
  };
}

function toApiCampaignQuota({ id, ...quota }: ChannelCampaignQuota): ChannelCampaignQuotaRequestDto {
  return { ...quota, ...(id ? { id } : {}) };
}

export function fromApiMessageCampaign(response: MessageCampaignResponseDto): MessageCampaignSettings {
  return {
    delayInSeconds: response.delay_in_seconds,
    languageId: response.language_id,
    message: response.message,
    providerId: response.sms_provider_interface_id,
    quota: fromApiCampaignQuota(response.quota),
  };
}

export function toApiMessageCampaign(campaign: MessageCampaignSettings): MessageCampaignRequestDto {
  return {
    delay_in_seconds: campaign.delayInSeconds,
    language_id: campaign.languageId,
    message: campaign.message,
    quota_attributes: toApiCampaignQuota(campaign.quota),
    sms_provider_interface_id: campaign.providerId,
  };
}

export function fromApiSmsCampaign(response: SmsCampaignResponseDto): SmsCampaignSettings {
  return {
    ...fromApiMessageCampaign(response),
    twoWayThreshold: { enabled: response.two_way_sms_threshold_enabled, hours: response.two_way_sms_threshold },
  };
}

export function toApiSmsCampaign({ twoWayThreshold, ...campaign }: SmsCampaignSettings): SmsCampaignRequestDto {
  return {
    ...toApiMessageCampaign(campaign),
    two_way_sms_threshold: twoWayThreshold.hours,
    two_way_sms_threshold_enabled: twoWayThreshold.enabled,
  };
}

export function fromApiEmailCampaign(response: EmailCampaignResponseDto): EmailCampaignSettings {
  return {
    htmlTemplate: response.html_template,
    languageId: response.language_id,
    providerId: response.mail_provider_interface_id,
    quota: fromApiCampaignQuota(response.quota),
    subject: response.subject,
  };
}

export function toApiEmailCampaign(campaign: EmailCampaignSettings): EmailCampaignRequestDto {
  return {
    html_template: campaign.htmlTemplate,
    language_id: campaign.languageId,
    mail_provider_interface_id: campaign.providerId,
    quota_attributes: toApiCampaignQuota(campaign.quota),
    subject: campaign.subject,
  };
}

export function fromApiCatiCall(response: CatiCallDto): CatiCallSettings {
  return { callLimit: response.cati_call_limit, telephoneProviderId: response.telephone_provider_interface_id };
}

export function toApiCatiCall(catiCall: CatiCallSettings): CatiCallDto {
  return { cati_call_limit: catiCall.callLimit, telephone_provider_interface_id: catiCall.telephoneProviderId };
}

export function toApiChannelProvidersUrl({ accountId, kind }: GetChannelProvidersArgs): string {
  return `/v1/accounts/${accountId}/settings/${kind}_provider_interfaces`;
}

// An unnamed provider is shown by its type, e.g. `twilio`.
export function fromApiChannelProviders(response: ChannelProviderDto[]): ChannelProvider[] {
  return response.map(({ id, name, provider }) => ({ id, name: name || String(provider ?? id) }));
}
